<template>
  <section
    id="styling"
    class="padding-top-5xl"
  >
    <ais-instant-search
      :search-client="searchClient"
      :index-name="index"
      :future="{ preserveSharedStateOnUnmount: true }"
    >
      <ais-configure
        :distinct="true"
        :facet-filters.camel="facetFilters()"
      />

      <ais-hits
        v-if="iconId != null"
        :transform-items="transformItems"
        class="app-content"
        role="main"
      >
        <div
          v-if="items.length > 0"
          slot="default"
          slot-scope="{ items }"
        >
          <div class="row margin-bottom-2xl">
            <div class="column-12 text-center">
              <h2
                class="text-uppercase size-md with-anchor"
                style="letter-spacing: var(--letter-spacing-3xl); font-weight: var(--font-weight-black); color: var(--fa-md-gravy)"
              >
                <anchor-link
                  anchor="#styling"
                  link-color="gravy"
                  anchor-name="Styling Toolkit"
                />
                Seasoned to Perfection
              </h2>
              <h2 class="margin-y-md">Styling Toolkit</h2>
              <p class="line-length-xl margin-auto">
                We've seasoned our styling tools to perfection, making icons more flexible right out of the box. With just a pinch of code, you can size, place,
                style, and even animate any icon for the perfect look.
              </p>
            </div>
          </div>
          <div class="row margin-bottom-2xl">
            <div class="column-12 text-center">
              <ul
                class="display-flex"
                style="
                  --list-style-type: none;
                  gap: var(--spacing-md);
                  justify-content: center;
                  --button-padding: var(--button-padding-vertical) var(--spacing-md);
                "
              >
                <li>
                  <button
                    :class="{ active: isActive('house') }"
                    @click="iconName = 'house'"
                  >
                    <font-awesome-icon
                      :icon="$v7.solid.faHouse"
                      size="lg"
                    /><span class="sr-only">house icon</span>
                  </button>
                </li>
                <li>
                  <button
                    :class="{ active: isActive('face-smile') }"
                    @click="iconName = 'face-smile'"
                  >
                    <font-awesome-icon
                      :icon="$v7.solid.faFaceSmile"
                      size="lg"
                    /><span class="sr-only">face-smile icon</span>
                  </button>
                </li>
                <li>
                  <button
                    :class="{ active: isActive('truck') }"
                    @click="iconName = 'truck'"
                  >
                    <font-awesome-icon
                      :icon="$v7.solid.faTruck"
                      size="lg"
                    /><span class="sr-only">truck icon</span>
                  </button>
                </li>
                <li>
                  <button
                    :class="{ active: isActive('image') }"
                    @click="iconName = 'image'"
                  >
                    <font-awesome-icon
                      :icon="$v7.solid.faImage"
                      size="lg"
                    /><span class="sr-only">image icon</span>
                  </button>
                </li>
                <li>
                  <button
                    :class="{ active: isActive('music') }"
                    @click="iconName = 'music'"
                  >
                    <font-awesome-icon
                      :icon="$v7.solid.faMusic"
                      size="lg"
                    /><span class="sr-only">music icon</span>
                  </button>
                </li>
                <li>
                  <button
                    :class="{ active: isActive('file') }"
                    @click="iconName = 'file'"
                  >
                    <font-awesome-icon
                      :icon="$v7.solid.faFile"
                      size="lg"
                    /><span class="sr-only">file icon</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div class="card text-left">
            <div class="wrap-icon-details row roomy align-stretch">
              <div class="icon-detail-grid-preview column-12 margin-bottom-2xl laptop:margin-bottom-0 laptop:column-6">
                <icon-preview
                  :preview-mode="styling.previewMode"
                  :styling="styling"
                  :icon="icon"
                  :default-styles="defaultStyles"
                  :display-icon-preview-controls="true"
                  :major-version="activeMajorVersion"
                  :is-duotone="isDuotoneIcon"
                  @resetStyles="resetStyles"
                  @changePreviewMode="changePreviewMode"
                  @toggleStyleOptions="toggleStyleOptions"
                  @toggleAnimationOptions="toggleAnimationOptions"
                />
              </div>

              <div class="icon-detail-use column-12 laptop:column-6 margin-bottom-2xl laptop:margin-bottom-0">
                <div class="margin-bottom-2xl">
                  <icon-pack-selector
                    :icon="icon"
                    :styling="styling"
                    :major-version="activeMajorVersion"
                    :default-styles="defaultStyles"
                    @change="(newStyling) => (styling = newStyling)"
                  />
                </div>

                <icon-code-snippet
                  :id="icon.name"
                  :can-download-icon="false"
                  :default-styles="defaultStyles"
                  :icon="icon"
                  :icon-abstract="iconAbstract"
                  :is-duotone="isDuotoneIcon"
                  :is-using-modal="false"
                  :showing="showing"
                  :is-hiding-svg="true"
                  :styling="styling"
                  :viggo="viggo"
                  @show="changeShowing"
                  @getSelectedAction="getSelectedAction"
                />

                <div
                  class="display-flex flex-row flex-wrap flex-items-stretch icon-actions"
                  style="--button-margin-bottom: 0; gap: var(--spacing-md)"
                >
                  <site-link
                    :to="{ name: 'start' }"
                    class="button accent position-relative icon-action-start-using"
                    aria-label="Set up your web project to use this icon!"
                    style="top: calc(var(--button-box-shadow-width) * -1)"
                    data-balloon-pos="up"
                  >
                    <font-awesome-icon
                      :icon="$v7.regular.faRocketLaunch"
                      size="xl"
                    />
                    <span class="margin-left-2xs">Start Using This Icon</span>
                  </site-link>
                  <site-link
                    v-if="!isProPlusIcon"
                    :to="$docs('/web/add-icons/icon-wizard')"
                    class="button position-relative icon-action-customize"
                    aria-label="Magically add modifiers to this icon!"
                    data-balloon-pos="up"
                    style="top: calc(var(--button-box-shadow-width) * -1); flex-grow: 0; padding-right: var(--spacing-md)"
                  >
                    <font-awesome-icon
                      :icon="$v7.solid.faSquareDashedCirclePlus"
                      size="xl"
                    />
                    <span class="sr-only">Add Modifiers with the Icon Wizard</span>
                  </site-link>
                </div>
              </div>

              <div class="icon-detail-styling-tabs column-12">
                <icon-styling
                  v-show="isShowingStyleOptions"
                  :icon="icon"
                  :styling="styling"
                  :major-version="activeMajorVersion"
                  :default-styles="defaultStyles"
                  @change="(newStyling) => (styling = newStyling)"
                />

                <icon-animations
                  v-show="isShowingAnimationOptions"
                  :icon="icon"
                  :styling="styling"
                  :major-version="activeMajorVersion"
                  @change="(newStyling) => (styling = newStyling)"
                />
              </div>
            </div>
          </div>
        </div>
      </ais-hits>
    </ais-instant-search>
  </section>
</template>

<script>
  import { get, isEmpty } from 'lodash'

  import { loadViggo } from 'app/viggo'
  import { fetchIndexName, getSharedSearchClient } from 'app/views/search/utils'
  import { IconsMixins } from 'app/views/icons/components/icons/mixins'
  import { isProPlusIcon } from 'app/views/icons/iconAccess'

  import IconAnimations from 'app/views/icons/components/icons/details/IconAnimations'
  import IconCodeSnippet from 'app/views/icons/components/icons/details/IconCodeSnippet'
  import IconPackSelector from 'app/views/icons/components/icons/details/IconPackSelector'
  import IconPreview from 'app/views/icons/components/icons/details/IconPreview'
  import IconStyling from 'app/views/icons/components/icons/details/IconStyling'
  import AnchorLink from '../components/AnchorLink'
  import { getLastColor, getOpacity, enableColorSync } from 'app/views/search/colorUtils'

  import { DEFAULT_STYLING } from 'app/views/icons/defaults'

  export default {
    name: 'Styling',

    components: {
      IconAnimations,
      IconCodeSnippet,
      IconPackSelector,
      IconPreview,
      IconStyling,
      AnchorLink
    },

    mixins: [IconsMixins],

    data() {
      return {
        iconName: 'face-smile',
        hasCategories: true,
        hasAliases: false,
        isBrandsIcon: false,
        isSponsored: false,
        display: 'detail',
        doesNotExist: false,
        icon: { membership: { free: [], pro: [] }, aliases: {}, styles: [], sponsored: {}, changes: [] },
        searchClient: getSharedSearchClient(),
        iconAbstract: null,
        isLoading: false,
        showing: 'html',
        isShowingUpsellModal: false,
        isShowingStyleOptions: true,
        isShowingAnimationOptions: false,
        styling: {
          animation: DEFAULT_STYLING.animation,
          colorPrimary: DEFAULT_STYLING.colorPrimary,
          colorSecondary: DEFAULT_STYLING.colorSecondary,
          family: 'classic',
          opacityPrimary: DEFAULT_STYLING.opacityPrimary,
          opacitySecondary: DEFAULT_STYLING.opacitySecondary,
          rotation: DEFAULT_STYLING.rotation,
          size: DEFAULT_STYLING.size,
          style: 'solid',
          previewMode: 'day'
        },
        viggo: null,
        selectedAction: 'Whoopsie! That action'
      }
    },

    computed: {
      index() {
        return fetchIndexName(this.$releases.active.version)
      },

      iconId() {
        return this.iconName
      },

      objectID() {
        const family = get(this, ['styling', 'family'], 'classic') // default to classic if not set
        const style = get(this, ['styling', 'style'], 'solid') // default to solid if not set

        return `i:${family}:${style}:${this.iconId}`
      },

      isProIcon() {
        return !this.icon.is_free
      },

      isProPlusIcon() {
        return isProPlusIcon(this.icon)
      },

      activeMajorVersion() {
        return this.$releases.active.versionParts.major
      },

      defaultStyles() {
        const stylingDefaults = { ...DEFAULT_STYLING }

        stylingDefaults.family = this.styling.family
        stylingDefaults.style = this.styling.style

        return stylingDefaults
      }
    },

    watch: {
      icon(newValue, oldValue) {
        const overrides = {}

        if (this.showing === 'svg' && !this.canAccessIcon) {
          this.showing = 'html'
        }

        if (newValue.family === 'brands' || newValue.style === 'brands') {
          if (!this.styling.family && newValue.family) {
            overrides.family = newValue.family
          }

          const normalizedIcon = this.normalizeBrandsIcon(newValue)
          if (normalizedIcon.family !== this.styling.family || normalizedIcon.style !== this.styling.style) {
            overrides.family = normalizedIcon.family
            overrides.style = normalizedIcon.style
          }

          if (!isEmpty(overrides)) {
            this.styling = { ...this.styling, ...overrides }
          }
        }
      }
    },

    async created() {
      this.isLoading = true
      if (!this.viggo) {
        try {
          this.viggo = await loadViggo()
        } catch (error) {
          throw new Error('Error loading viggo', error)
        }
      }

      this.loadColors()
      this.isLoading = false
    },

    methods: {
      loadColors() {
        const primaryColor = getLastColor(true)
        const secondaryColor = getLastColor(false)

        this.stylingChange({
          colorPrimary: primaryColor,
          opacityPrimary: getOpacity(primaryColor),
          colorSecondary: secondaryColor,
          opacitySecondary: getOpacity(secondaryColor)
        })
      },

      facetFilters() {
        // Find by icon name OR alias using Algolia's AND/OR syntax. See the following doc for explanation:
        // https://www.algolia.com/doc/guides/managing-results/refine-results/filtering/in-depth/filters-and-facetfilters/#syntax-difference-between-filters-and-facetfilters
        // Brands was added so brand icons will load without "?f=brands" query string
        return [
          [
            // search for the objectID OR alias name
            `objectID:${this.objectID}`,
            `objectID:i:brands:${this.iconId}`,
            `aliases.names:${this.iconId}`
          ]
        ]
      },

      transformItems(items) {
        if (items.length >= 1) {
          this.icon = items[0]
        }

        return items
      },

      getSelectedAction(action) {
        this.selectedAction = action

        return this.selectedAction
      },

      changePreviewMode(mode) {
        return (this.styling.previewMode = mode)
      },

      changeShowing(snippet) {
        if (snippet === 'svg' && !this.canAccessIcon) {
          this.selectedAction = `Downloading this Icon`
          return this.openProIconModal()
        } else {
          this.showing = snippet
        }
      },

      resetStyles() {
        this.styling = { ...this.styling, ...DEFAULT_STYLING }
        enableColorSync()
      },

      stylingChange(styling) {
        this.styling = { ...this.styling, ...styling }
      },

      toggleStyleOptions() {
        this.isShowingAnimationOptions = false
        this.isShowingStyleOptions = !this.isShowingStyleOptions
      },

      toggleAnimationOptions() {
        this.isShowingStyleOptions = false
        this.isShowingAnimationOptions = !this.isShowingAnimationOptions
      },

      isActive(name) {
        return this.iconName === name
      }
    }
  }
</script>
